/**
 * Declarative renderer for the reader page text.
 *
 * Renders the parsed token tree (see readerText/tokens.ts) with translation
 * groups, selection highlights, and audio highlights derived from
 * ReaderTextStore signals.
 *
 * Everything here is built so that the cost of an interaction is proportional
 * to what the user touched rather than to the length of the text - scroll mode
 * renders a whole book (tens of thousands of word spans) as a single block, so
 * anything that re-renders a block per translation stalls the main thread for
 * seconds. Blocks are therefore split into segments of a few hundred items, and
 * each segment subscribes only to the groups overlapping it; per-word classes
 * come from per-position signals the store pushes to.
 *
 * Pointer handling is delegated: one set of handlers on the content container
 * (plus document-level move/up listeners while dragging) replaces the old
 * per-span jQuery bindings.
 */
import { effect } from "@preact/signals";
import $ from "jquery";
import * as preact from "preact";

import * as pageSwipe from "pageSwipe";
import * as readerTokens from "readerText/tokens";
import * as utils from "shared/utils";
import type ReaderTextStore from "stores/ReaderTextStore";
import {
  groupTranslationText,
  type ReaderTextGroup,
} from "stores/ReaderTextStore";
import * as vScroll from "vScroll";

type PointerTarget = {
  position?: number;
  groupId?: number;
  element: HTMLElement;
};

const pointerTargetFromNode = (
  node: EventTarget | null
): PointerTarget | null => {
  // Duck-typed rather than `instanceof Element` so it also works in the test
  // environment, where DOM classes aren't Node globals.
  const element =
    (node as Element | null)?.closest?.<HTMLElement>(
      "span[data-p], span[data-group]"
    ) ?? null;
  if (!element) {
    return null;
  }
  const position = element.getAttribute("data-p");
  if (position !== null) {
    return { position: parseInt(position, 10), element };
  }
  return {
    groupId: parseInt(element.getAttribute("data-group") ?? "", 10),
    element,
  };
};

/**
 * Base for components that render from store signals. Class components don't
 * auto-subscribe to signals here, so subclasses declare the signals they read
 * via `watchedSignals()` and are re-rendered whenever one changes. The
 * subscription is bound on mount, which is why these components must be keyed
 * by `store.instanceKey` (directly or via an ancestor) - a swapped-in store
 * must remount them to re-subscribe.
 */
abstract class SignalWatcher<Props> extends preact.Component<Props> {
  private disposeWatchEffect?: () => void;

  /** Read (and thereby subscribe to) every signal the render depends on. */
  protected abstract watchedSignals(): void;

  componentDidMount() {
    let initialRun = true;
    this.disposeWatchEffect = effect(() => {
      this.watchedSignals();
      if (initialRun) {
        initialRun = false;
        return;
      }
      this.forceUpdate();
    });
  }

  componentWillUnmount() {
    this.disposeWatchEffect?.();
  }
}

class WordSpanView extends SignalWatcher<{
  store: ReaderTextStore;
  token: readerTokens.WordToken;
}> {
  protected watchedSignals() {
    void this.props.store.wordClass(this.props.token.position).value;
  }

  render() {
    const { store, token } = this.props;
    const className = store.wordClass(token.position).value;
    return (
      <span data-p={token.position} class={className || undefined}>
        {token.text}
      </span>
    );
  }
}

/**
 * Render the original tokens of a group's range (shown inside the group span
 * and under the translation). Word spans are rendered without `data-p`,
 * mirroring the old display-clone behavior: the canonical position attributes
 * must stay unique in the document.
 */
const renderStrippedEntries = (
  store: ReaderTextStore,
  entries: RenderEntry[],
  depth: number
): preact.ComponentChildren[] => {
  return buildNodes(entries, depth, (entry) => {
    if (entry.kind !== "item") {
      return null;
    }
    const token = entry.item.token;
    if (token.kind === "word") {
      const yourWord = store.yourPhrases.value.has(token.position);
      return (
        <span
          key={`w${token.position}`}
          class={yourWord ? "yourWord" : undefined}
        >
          {token.text}
        </span>
      );
    }
    return renderLeafToken(token, `t${entry.item.index}`);
  });
};

class GroupSpanView extends SignalWatcher<{
  store: ReaderTextStore;
  group: ReaderTextGroup;
  entries: RenderEntry[];
  depth: number;
}> {
  protected watchedSignals() {
    const { store, group } = this.props;
    void store.selectingGroupIds.value;
    void store.yourPhrases.value;
    if (group.from === group.to) {
      void store.wordClass(group.from).value;
    }
  }

  render() {
    const { store, group, entries, depth } = this.props;
    const directions = store.getDirections();
    const directionsDiffer =
      directions && directions.source !== directions.target;
    // languageMap directions are upper-case ("RTL"/"LTR"); the dir attribute's
    // canonical (and Preact-typed) values are lower-case.
    const sourceDir = (directions?.source || "LTR").toLowerCase() as
      | "ltr"
      | "rtl";
    const targetDir = (directions?.target || "LTR").toLowerCase() as
      | "ltr"
      | "rtl";

    const selecting = store.selectingGroupIds.value.has(group.id);
    const singleWord = group.from === group.to;
    const wordClasses = singleWord
      ? store.wordClass(group.from).value
      : undefined;

    const className =
      utils.cn({
        translating: true,
        translated: group.status === "translated",
        monolingual: group.monolingual,
        selecting,
        deleting: group.struck,
      }) + (wordClasses ? " " + wordClasses : "");

    const originalContent = renderStrippedEntries(store, entries, depth);

    const showGloss =
      group.status === "translated" &&
      !group.monolingual &&
      group.translation !== undefined;

    const attributes: Record<string, unknown> = singleWord
      ? { "data-p": group.from }
      : { "data-group": group.id };

    if (directionsDiffer) {
      // The wrapper sits inline within the source-language flow, so its `dir`
      // must match the source direction - otherwise an RTL paragraph that
      // contains an LTR-attributed wrapper gets re-ordered by the browser's
      // bidi algorithm (forum #5052). The `.translation` and `.original`
      // children are then explicitly anchored to their own languages.
      attributes.dir = sourceDir;
    }

    if (!showGloss) {
      return (
        <span {...attributes} class={className}>
          {originalContent}
        </span>
      );
    }

    const translationText = groupTranslationText(group);

    return (
      <span {...attributes} class={className}>
        <div class="translation" dir={directionsDiffer ? targetDir : undefined}>
          {group.transliteration !== undefined ? (
            <>
              <div class="transliteration">{group.transliteration}</div>
              <div>{translationText}</div>
            </>
          ) : (
            translationText
          )}
        </div>
        <div class="original" dir={directionsDiffer ? sourceDir : undefined}>
          {originalContent}
        </div>
      </span>
    );
  }
}

const attributesToProps = (attributes: {
  [name: string]: string;
}): Record<string, string> => {
  const props: Record<string, string> = {};
  for (const [name, value] of Object.entries(attributes)) {
    props[name] = value;
  }
  return props;
};

const renderLeafToken = (
  token: readerTokens.ReaderToken,
  key: string
): preact.ComponentChildren => {
  if (token.kind === "text") {
    return token.text;
  }
  if (token.kind === "element") {
    return preact.h(token.tag, {
      ...attributesToProps(token.attributes),
      key,
    });
  }
  return null;
};

type RenderEntry =
  | {
      kind: "item";
      item: readerTokens.ReaderTextItem;
      chain: readerTokens.ElementToken[];
    }
  | {
      kind: "group";
      group: ReaderTextGroup;
      entries: RenderEntry[];
      chain: readerTokens.ElementToken[];
      depth: number;
    };

/**
 * Rebuild the minimal element tree for a flat run of entries. Consecutive
 * entries sharing ancestor elements are merged under one rendered element;
 * this is what makes a phrase group that starts mid-`<strong>` render with the
 * wrapper split at exactly the right boundary (the old DOM-splitting
 * `spanUtils.splitBoundaryBefore/After`, now a pure render-time computation).
 */
const buildNodes = <Entry extends { chain: readerTokens.ElementToken[] }>(
  entries: Entry[],
  depth: number,
  renderLeaf: (entry: Entry) => preact.ComponentChildren
): preact.ComponentChildren[] => {
  const nodes: preact.ComponentChildren[] = [];
  let index = 0;
  while (index < entries.length) {
    const entry = entries[index];
    if (entry.chain.length <= depth) {
      nodes.push(renderLeaf(entry));
      index++;
      continue;
    }
    const element = entry.chain[depth];
    let runEnd = index;
    while (
      runEnd < entries.length &&
      entries[runEnd].chain.length > depth &&
      entries[runEnd].chain[depth] === element
    ) {
      runEnd++;
    }
    nodes.push(
      preact.h(
        element.tag,
        { ...attributesToProps(element.attributes), key: `e${element.id}` },
        buildNodes(entries.slice(index, runEnd), depth + 1, renderLeaf)
      )
    );
    index = runEnd;
  }
  return nodes;
};

/**
 * Number of items a segment aims to cover. Segments are the unit that subscribes
 * to group changes and re-renders, so this trades the cost of re-rendering one
 * segment against the number of segment components in a block. It matters because
 * a scroll-mode text is a single block of the whole book: rendering that block
 * as one unit made every translation diff tens of thousands of word spans,
 * blocking the main thread for seconds (forum #5245).
 */
const SEGMENT_TARGET_ITEMS = 200;

type Segment = {
  /** Ancestor elements the block renders around this segment. */
  chain: readerTokens.ElementToken[];
  /** Inclusive index range within the block's items. */
  start: number;
  end: number;
};

const hasChainPrefix = (
  chain: readerTokens.ElementToken[],
  prefix: readerTokens.ElementToken[]
) =>
  prefix.length <= chain.length &&
  prefix.every((element, depth) => chain[depth] === element);

/**
 * Split a block's items into segments, each starting at the top level of its
 * block root so that a phrase group - which renders as a child of that root -
 * always lands at its segment's own depth. Items nested deeper (inside an
 * `<i>`, or inside a block nested in this one) stay with the segment that owns
 * their root, and a segment ends early when an item leaves that root.
 *
 * Boundaries depend only on the token structure - never on groups - so
 * segment keys stay stable and a straddling group redraws around a boundary
 * (see ReaderTextSegment) rather than moving it.
 */
const splitIntoSegments = (items: readerTokens.ReaderTextItem[]): Segment[] => {
  const segments: Segment[] = [];
  for (const [index, item] of items.entries()) {
    const current = segments[segments.length - 1];
    // A segment may only start at an item sitting directly at its block root
    // (a group renders as a child of that root), so a full segment runs on
    // past the target until the next item outside any inline element.
    const atBlockRoot = item.chain.length === item.blockDepth;
    const currentIsFull =
      current !== undefined && index - current.start >= SEGMENT_TARGET_ITEMS;
    if (
      current &&
      hasChainPrefix(item.chain, current.chain) &&
      !(atBlockRoot && currentIsFull)
    ) {
      current.end = index;
      continue;
    }
    segments.push({
      chain: item.chain.slice(0, item.blockDepth),
      start: index,
      end: index,
    });
  }
  return segments;
};

export const _functionsForUnitTests = {
  splitIntoSegments,
  SEGMENT_TARGET_ITEMS,
};

type BlockProps = {
  store: ReaderTextStore;
  items: readerTokens.ReaderTextItem[];
};

class ReaderTextBlock extends preact.Component<BlockProps> {
  render() {
    const { store, items } = this.props;
    return (
      <>
        {/* buildNodes merges adjacent segments sharing a chain element back
            into one wrapper, so the rendered DOM is identical to an
            unsegmented render. */}
        {buildNodes(splitIntoSegments(items), 0, (segment) => (
          <ReaderTextSegment
            key={`s${items[segment.start].index}-${items[segment.end].index}`}
            store={store}
            items={items}
            start={segment.start}
            end={segment.end}
            depth={segment.chain.length}
          />
        ))}
      </>
    );
  }
}

type SegmentProps = {
  store: ReaderTextStore;
  /** Every item of the enclosing block; this segment renders its own range. */
  items: readerTokens.ReaderTextItem[];
  /** Inclusive index range within `items`. */
  start: number;
  end: number;
  /** Depth of the ancestor elements the block already rendered. */
  depth: number;
};

class ReaderTextSegment extends SignalWatcher<SegmentProps> {
  private groupsSignal() {
    const { store, items, start, end } = this.props;
    return store.groupsForItemRange(items[start].index, items[end].index);
  }

  protected watchedSignals() {
    void this.groupsSignal().value;
    void this.props.store.renderFromPosition.value;
  }

  render() {
    const { store, items, start, end, depth } = this.props;
    const parsed = store.tokens.value;
    if (!parsed) {
      return null;
    }

    // A block's items are a contiguous run of the parsed item list, so a
    // parsed item index minus `blockOffset` is that item's offset into
    // `items` - the same space as `start`/`end`. Group ranges arrive in
    // parsed indexes and are converted here; everything below compares in
    // offset space.
    const blockOffset = items[0].index;
    const intervals = [];
    for (const group of this.groupsSignal().value) {
      const range = readerTokens.getItemRange(parsed, group);
      if (range) {
        intervals.push({
          group,
          startOffset: range.startIndex - blockOffset,
          endOffset: range.endIndex - blockOffset,
        });
      }
    }

    // A group can straddle a segment boundary: the segment holding its first word
    // renders the whole group, and the segment after skips what it covered.
    // Safe because both segments overlap the group, so both subscribe to it
    // and re-render in the same update - the seam can't go stale.
    let firstOffset = start;
    let lastOffset = end;
    for (const interval of intervals) {
      if (interval.startOffset < start) {
        firstOffset = Math.max(firstOffset, interval.endOffset + 1);
      }
      if (interval.endOffset > end) {
        lastOffset = Math.max(lastOffset, interval.endOffset);
      }
    }
    if (firstOffset > lastOffset) {
      return null;
    }

    let visibleItems = items.slice(firstOffset, lastOffset + 1);

    const renderFrom = store.renderFromPosition.value;
    const startsAboveWindow = visibleItems.some(
      (item) => item.token.kind === "word" && item.token.position < renderFrom
    );
    if (startsAboveWindow) {
      const firstVisibleIndex = visibleItems.findIndex(
        (item) =>
          item.token.kind === "word" && item.token.position >= renderFrom
      );
      if (firstVisibleIndex === -1) {
        return null;
      }
      visibleItems = visibleItems.slice(firstVisibleIndex);
    }

    // Partition the segment's items into plain entries and group runs. A group
    // run becomes a synthetic entry whose chain is truncated at the block
    // root, so the group span renders as a direct child of the block element
    // (exactly where the old DOM surgery used to mount it).
    const entries: RenderEntry[] = [];
    let index = 0;
    while (index < visibleItems.length) {
      const item = visibleItems[index];
      const itemOffset = item.index - blockOffset;
      const interval = intervals.find(
        (candidate) =>
          candidate.startOffset <= itemOffset &&
          itemOffset <= candidate.endOffset
      );
      if (!interval) {
        entries.push({ kind: "item", item, chain: item.chain });
        index++;
        continue;
      }
      const runEntries: RenderEntry[] = [];
      const firstRunItem = visibleItems[index];
      while (
        index < visibleItems.length &&
        visibleItems[index].index - blockOffset <= interval.endOffset
      ) {
        const runItem = visibleItems[index];
        runEntries.push({ kind: "item", item: runItem, chain: runItem.chain });
        index++;
      }
      const blockDepthChain = firstRunItem.chain.slice(
        0,
        firstRunItem.blockDepth
      );
      entries.push({
        kind: "group",
        group: interval.group,
        entries: runEntries,
        chain: blockDepthChain,
        depth: blockDepthChain.length,
      });
    }

    return (
      <>
        {buildNodes(entries, depth, (entry) => {
          if (entry.kind === "group") {
            return (
              <GroupSpanView
                key={`g${entry.group.id}`}
                store={store}
                group={entry.group}
                entries={entry.entries}
                depth={entry.depth}
              />
            );
          }
          const token = entry.item.token;
          if (token.kind === "word") {
            return (
              <WordSpanView
                key={`w${token.position}`}
                store={store}
                token={token}
              />
            );
          }
          return renderLeafToken(token, `t${entry.item.index}`);
        })}
      </>
    );
  }
}

type ContentProps = {
  store: ReaderTextStore;
  /**
   * Passed as a prop (rather than read from store.isScrollMode()) because the
   * store's options are only set up after the page's render commits, which is
   * after this component mounts and binds vScroll to the viewport.
   */
  scrollMode?: boolean;
  /** A quick sideways swipe across the page (not in scroll mode) */
  onSwipe?: (direction: pageSwipe.SwipeDirection) => void;
};

export default class ReaderTextContent extends SignalWatcher<ContentProps> {
  private containerElement?: HTMLElement;
  private viewportElement?: HTMLElement | null;
  /** The page's column, margins included, which a swipe may start anywhere on */
  private swipeAreaElement?: HTMLElement | null;
  private gestureTarget?: EventTarget;
  private readonly pageSwipe = new pageSwipe.PageSwipe();
  /**
   * A touch on a word, held back while it may yet be a swipe turning the page
   * (so a swipe doesn't light up the words it crosses): where it started and
   * is now
   */
  private heldTouch?: {
    start: PointerTarget;
    latest: PointerTarget | null;
    timer: number;
  };

  protected watchedSignals() {
    void this.props.store.tokens.value;
  }

  componentDidMount() {
    super.componentDidMount();
    document.addEventListener("mousemove", this.onDocumentMouseMove);
    document.addEventListener("mouseup", this.onDocumentMouseUp);

    this.viewportElement = this.containerElement?.closest("#pageTextViewport");
    this.setupVScrollIfNecessary();
    this.viewportElement?.addEventListener("touchmove", this.onTouchMove);
    this.viewportElement?.addEventListener("touchend", this.onTouchEnd);
    // Capturing: a touch on a word stops its touchstart going further, and a
    // swipe's touchend mustn't reach the word as a tap
    this.swipeAreaElement =
      this.containerElement?.closest(".bookColumn") ?? this.viewportElement;
    this.swipeAreaElement?.addEventListener(
      "touchstart",
      this.onSwipeAreaTouchStart,
      { capture: true, passive: true }
    );
    this.swipeAreaElement?.addEventListener(
      "touchend",
      this.onSwipeAreaTouchEnd,
      { capture: true }
    );
  }

  componentDidUpdate() {
    this.setupVScrollIfNecessary();
  }

  private setupVScrollIfNecessary() {
    if (this.props.scrollMode && this.viewportElement) {
      vScroll.setup({ element: $(this.viewportElement) });
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.dropHeldTouch();
    document.removeEventListener("mousemove", this.onDocumentMouseMove);
    document.removeEventListener("mouseup", this.onDocumentMouseUp);
    this.viewportElement?.removeEventListener("touchmove", this.onTouchMove);
    this.viewportElement?.removeEventListener("touchend", this.onTouchEnd);
    this.swipeAreaElement?.removeEventListener(
      "touchstart",
      this.onSwipeAreaTouchStart,
      { capture: true }
    );
    this.swipeAreaElement?.removeEventListener(
      "touchend",
      this.onSwipeAreaTouchEnd,
      { capture: true }
    );
    this.unbindGestureTarget();
  }

  /**
   * Browsers lock a touch gesture's events to the element where the touch
   * started. When a mid-drag re-render (e.g. the line nudge dropping the top
   * line) detaches that element, its events keep firing on the detached node
   * but no longer bubble to the viewport listeners, freezing the drag. These
   * per-gesture listeners on the touch target itself keep receiving the
   * locked events; they defer to the viewport listeners while the target is
   * still attached (to avoid double handling).
   */
  private bindGestureTarget(target: EventTarget) {
    this.unbindGestureTarget();
    this.gestureTarget = target;
    target.addEventListener("touchmove", this.onGestureTargetTouchMove);
    target.addEventListener("touchend", this.onGestureTargetTouchEnd);
  }

  private unbindGestureTarget() {
    this.gestureTarget?.removeEventListener(
      "touchmove",
      this.onGestureTargetTouchMove
    );
    this.gestureTarget?.removeEventListener(
      "touchend",
      this.onGestureTargetTouchEnd
    );
    this.gestureTarget = undefined;
  }

  private onGestureTargetTouchMove = (event: Event) => {
    if ((event.currentTarget as Node).isConnected) {
      return;
    }
    this.onTouchMove(event as TouchEvent);
  };

  private onGestureTargetTouchEnd = (event: Event) => {
    const connected = (event.currentTarget as Node).isConnected;
    this.unbindGestureTarget();
    if (connected) {
      return;
    }
    this.onTouchEnd(event as TouchEvent);
  };

  render() {
    const parsed = this.props.store.tokens.value;
    return (
      <div
        class="pageTextContent"
        ref={(element) => {
          this.containerElement = element ?? undefined;
        }}
        onMouseDown={this.onMouseDown}
        onMouseOver={this.onMouseOver}
        onMouseLeave={this.onMouseLeave}
        onTouchStart={this.onTouchStart}
        onClick={this.onClick}
      >
        {parsed?.itemsByBlock.map((blockItems, blockIndex) => (
          <ReaderTextBlock
            key={`b${blockIndex}`}
            store={this.props.store}
            items={blockItems}
          />
        ))}
      </div>
    );
  }

  private onMouseDown = (event: MouseEvent) => {
    const target = pointerTargetFromNode(event.target);
    if (!target || !this.props.store.enabled.value) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    this.props.store.dragStart(target, { byCharacter: event.shiftKey });
  };

  private onMouseOver = (event: MouseEvent) => {
    this.props.store.hoverWord(pointerTargetFromNode(event.target)?.position);
  };

  private onMouseLeave = () => {
    this.props.store.hoverWord(undefined);
  };

  private onDocumentMouseMove = (event: MouseEvent) => {
    if (!this.props.store.isDragging()) {
      return;
    }
    this.props.store.dragMove(pointerTargetFromNode(event.target));
  };

  private onDocumentMouseUp = (event: MouseEvent) => {
    if (!this.props.store.isDragging()) {
      return;
    }
    const target = pointerTargetFromNode(event.target);
    if (target) {
      event.stopPropagation();
      event.preventDefault();
    }
    // Releasing over a non-word still completes the selection from the last
    // valid drag position (a line nudge can move the text under a stationary
    // pointer, leaving it over a gap at release). A drag that moved off the
    // words has already had its dragTo cleared by dragMove(null), so this
    // ends up as a no-op there.
    this.props.store.dragEnd(target, target?.element);
  };

  private onClick = (event: MouseEvent) => {
    // Word/phrase clicks are fully handled on mousedown/mouseup; stop them
    // here so they don't bubble into page-level click handlers.
    if (pointerTargetFromNode(event.target) && this.props.store.enabled.value) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  private onTouchStart = (event: TouchEvent) => {
    const touches = event.touches;
    if (touches && touches.length > 1) {
      this.dropHeldTouch();
      this.props.store.cancelDrag();
      return;
    }

    if (this.props.store.isScrollMode() && touches) {
      vScroll.setStart(touches[0]);
    }

    const target = pointerTargetFromNode(event.target);
    if (!target || !this.props.store.enabled.value) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    if (this.props.onSwipe && !this.props.scrollMode) {
      this.holdTouch(target);
    } else if (!this.props.store.dragStart(target)) {
      return;
    }
    if (event.target) {
      this.bindGestureTarget(event.target);
    }
  };

  private holdTouch(start: PointerTarget) {
    this.dropHeldTouch();
    this.heldTouch = {
      start,
      latest: start,
      timer: window.setTimeout(
        this.releaseHeldTouch,
        pageSwipe.maxMilliseconds
      ),
    };
  }

  /** No swipe after all: the touch selects as it would have from the start */
  private releaseHeldTouch = () => {
    const heldTouch = this.heldTouch;
    this.dropHeldTouch();
    if (heldTouch && this.props.store.dragStart(heldTouch.start)) {
      if (heldTouch.latest !== heldTouch.start) {
        this.props.store.dragMove(heldTouch.latest);
      }
    }
  };

  private dropHeldTouch() {
    clearTimeout(this.heldTouch?.timer);
    this.heldTouch = undefined;
  }

  private targetFromTouchPoint(touch: Touch): PointerTarget | null {
    const x = touch.pageX - (document.scrollingElement?.scrollLeft ?? 0);
    const y = touch.pageY - (document.scrollingElement?.scrollTop ?? 0);
    return pointerTargetFromNode(document.elementFromPoint(x, y));
  }

  private onTouchMove = (event: TouchEvent) => {
    const store = this.props.store;
    if (store.isScrollMode() && vScroll.setMove(event.changedTouches[0])) {
      store.cancelDrag();
      return;
    }

    if (this.heldTouch) {
      if (event.touches.length > 1) {
        this.dropHeldTouch();
      } else {
        this.heldTouch.latest = this.targetFromTouchPoint(
          event.changedTouches[0]
        );
      }
      return;
    }

    if (!store.isDragging()) {
      // can get here e.g. after multi-touch gesture
      return;
    }

    if (event.touches.length > 1) {
      store.cancelDrag();
      return;
    }

    store.dragMove(this.targetFromTouchPoint(event.changedTouches[0]));
  };

  private onSwipeAreaTouchStart = (event: TouchEvent) => {
    if (this.props.onSwipe && !this.props.scrollMode) {
      this.pageSwipe.begin(event.touches, event.timeStamp);
    }
  };

  /** A swipe turns the page, rather than translating the words it crosses */
  private onSwipeAreaTouchEnd = (event: TouchEvent) => {
    const swipe = this.pageSwipe.end(event.changedTouches[0], event.timeStamp);
    if (!swipe) {
      return;
    }
    event.stopPropagation();
    // Not cancelable once the browser has taken the touch as a scroll
    if (event.cancelable) {
      event.preventDefault();
    }
    this.unbindGestureTarget();
    this.dropHeldTouch();
    this.props.store.cancelDrag();
    this.props.onSwipe?.(swipe);
  };

  private onTouchEnd = (event: TouchEvent) => {
    this.unbindGestureTarget();
    const store = this.props.store;
    this.releaseHeldTouch();
    if (store.isScrollMode() && vScroll.setEnd()) {
      event.stopPropagation();
      event.preventDefault();
      store.cancelDrag();
      return;
    }

    if (!store.isDragging()) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    // As with mouse-up: a null target (finger released over a gap, e.g.
    // after a line nudge moved the text under a stationary finger) still
    // completes the selection from the last valid drag position.
    const target = this.targetFromTouchPoint(event.changedTouches[0]);
    store.dragEnd(target, target?.element);
  };
}
